import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import type { MarketQuote, Product } from '@prody/shared';
import { marginBps, money } from '@prody/shared';
import {
  Button, Code, Delta, EmptyState, IconWarn, Input, ListPane, MoneyText,
  PageHeader, Segmented, Stamp, Toolbar, Workbench, toast,
} from '~/design-system';
import { useBusiness } from '~/platform/tenancy/BusinessProvider';
import { useProducts, useQuotes, useUpdateQuote } from '~/platform/data/queries';
import { productName } from '~/platform/domain/presentation';
import './market.css';

/* ==================================================================
   THE PRICE BOARD — the product's thesis screen.

   Everything in this business turns on one event: at first light the
   market sets today's prices and every sell price downstream moves
   with them. That event has never had a screen; it has a chalkboard,
   a phone call and a WhatsApp message to twelve chefs.

   So this is a chalkboard. Yesterday's sell price stays visible with
   an angled chalk strike through it, today's is set beside it, and a
   row that moved carries a mark. Nothing here is a "data grid with an
   editable column" — the mental model is the board a stallholder
   rubs out and rewrites at 04:00, and the interaction copies it: you
   type over the number in place and it is done.
   ================================================================== */

type Lens = 'all' | 'moved' | 'thin';

export default function MarketPage() {
  const { t, i18n } = useTranslation(['market', 'common']);
  const { business, format, can } = useBusiness();
  const tradingDay = format.tradingDay();

  const { data: quotes = [], isLoading } = useQuotes(business.id, tradingDay);
  const { data: products = [] } = useProducts(business.id);
  const updateQuote = useUpdateQuote(business.id, tradingDay);
  const [lens, setLens] = useState<Lens>('all');

  const productsById = useMemo(
    () => new Map<string, Product>(products.map((p) => [p.id as string, p])),
    [products],
  );

  const rows = useMemo(() => {
    const enriched = quotes.map((q) => ({
      quote: q,
      product: productsById.get(q.productId),
      moved: q.previousSell !== null && q.previousSell.minor !== q.sell.minor,
      margin: marginBps(q.sell, q.cost),
    }));
    if (lens === 'moved') return enriched.filter((r) => r.moved);
    if (lens === 'thin') return enriched.filter((r) => (r.margin ?? 10_000) < 1500);
    return enriched;
  }, [quotes, productsById, lens]);

  const movedCount = quotes.filter(
    (q) => q.previousSell !== null && q.previousSell.minor !== q.sell.minor,
  ).length;

  const canEdit = can('price', 'update');

  return (
    <Workbench>
      <PageHeader
        eyebrow={t('eyebrow')}
        title={t('title')}
        count={format.date(tradingDay, 'weekday')}
        actions={
          <>
            <Button variant="quiet">{t('copyYesterday')}</Button>
            <Button
              variant="primary"
              disabled={!canEdit}
              onClick={() =>
                toast.ok(t('published', { date: format.date(tradingDay, 'medium') }), {
                  detail: t('publishHint'),
                })
              }
            >
              {t('publish')}
            </Button>
          </>
        }
      />

      <Toolbar
        trailing={
          movedCount > 0 ? (
            <span className="pd-board__moved-count">
              <Delta bps={1} />
              {t('unpublished', { count: movedCount })}
            </span>
          ) : null
        }
      >
        <Segmented<Lens>
          label={t('common:actions.filter')}
          value={lens}
          onChange={setLens}
          options={[
            { value: 'all', label: t('common:labels.all') },
            { value: 'moved', label: t('change') },
            { value: 'thin', label: t('common:labels.margin') },
          ]}
        />
      </Toolbar>

      <ListPane>
        {isLoading ? (
          <div className="pd-board">
            {Array.from({ length: 6 }, (_, i) => (
              <div key={i} className="pd-board__row" aria-hidden="true">
                <span className="hatch" style={{ blockSize: '1.2em', inlineSize: '100%' }} />
              </div>
            ))}
          </div>
        ) : rows.length === 0 ? (
          <EmptyState
            title={t('empty')}
            body={t('emptyBody')}
            action={<Button variant="primary">{t('copyYesterday')}</Button>}
          />
        ) : (
          <div className="pd-board" role="table" aria-label={t('title')}>
            <div className="pd-board__head" role="row">
              <span className="stencil" role="columnheader">{t('common:labels.product')}</span>
              <span className="stencil right" role="columnheader">{t('todayCost')}</span>
              <span className="stencil right" role="columnheader">{t('yesterday')}</span>
              <span className="stencil right" role="columnheader">{t('todaySell')}</span>
              <span className="stencil right" role="columnheader">{t('common:labels.margin')}</span>
              <span className="stencil" role="columnheader">{t('availability')}</span>
            </div>

            {rows.map(({ quote, product, moved, margin }) => (
              <BoardRow
                key={quote.id}
                quote={quote}
                product={product}
                moved={moved}
                margin={margin}
                editable={canEdit}
                locale={i18n.resolvedLanguage ?? 'en'}
                onCommit={(sellMajor) =>
                  updateQuote.mutate({
                    quoteId: quote.id,
                    patch: { sell: money(sellMajor * 100, quote.sell.currency) },
                  })
                }
              />
            ))}
          </div>
        )}
      </ListPane>
    </Workbench>
  );
}

/* ------------------------------------------------------------------ *
 * One line of the board.
 *
 * The sell price is edited in place: click the number, type, blur.
 * There is no edit mode, no pencil icon and no save button, because
 * the thing this replaces is a person with a piece of chalk.
 * ------------------------------------------------------------------ */

function BoardRow({
  quote,
  product,
  moved,
  margin,
  editable,
  locale,
  onCommit,
}: {
  quote: MarketQuote;
  product: Product | undefined;
  moved: boolean;
  margin: number | null;
  editable: boolean;
  locale: string;
  onCommit: (sellMajor: number) => void;
}) {
  const { t } = useTranslation(['market', 'common']);
  const { format } = useBusiness();
  const [draft, setDraft] = useState<string | null>(null);

  const belowCost = quote.sell.minor <= quote.cost.minor;
  const changeBps =
    quote.previousSell && quote.previousSell.minor !== 0
      ? Math.round(((quote.sell.minor - quote.previousSell.minor) / quote.previousSell.minor) * 10_000)
      : null;

  const commit = () => {
    if (draft === null) return;
    const parsed = Number(draft.replace(',', '.'));
    setDraft(null);
    if (Number.isFinite(parsed) && parsed > 0) onCommit(parsed);
  };

  return (
    <div
      className="pd-board__row"
      role="row"
      data-hc={product?.handlingClass ?? 'neutral'}
      data-moved={moved || undefined}
      data-below-cost={belowCost || undefined}
    >
      <span className="pd-band" aria-hidden="true" />

      <div className="pd-board__product" role="cell">
        <span className="pd-board__name truncate">
          {product ? productName(product, locale) : quote.productId}
        </span>
        <span className="pd-board__meta">
          <Code>{product?.sku}</Code>
          {product?.traits.grading?.calibre ? (
            <span className="faint">· {t('catalog:calibre')} {product.traits.grading.calibre}</span>
          ) : null}
          {product?.traits.origin?.region ? (
            <span className="faint">· {product.traits.origin.region}</span>
          ) : null}
        </span>
      </div>

      <div className="pd-board__cost right" role="cell">
        <MoneyText value={quote.cost} bare size="sm" />
      </div>

      {/* Yesterday, struck through in chalk. The single most
          characteristic mark in the product. */}
      <div className="pd-board__was right" role="cell">
        {quote.previousSell ? (
          <span className={moved ? 'chalk-struck num' : 'num faint'}>
            {format.money(quote.previousSell, { bare: true })}
          </span>
        ) : (
          <span className="faint">—</span>
        )}
      </div>

      {/* Today, editable in place. */}
      <div className="pd-board__sell right" role="cell">
        {editable ? (
          <Input
            className="pd-board__input"
            numeric
            aria-label={`${t('todaySell')} ${product?.sku ?? ''}`}
            value={draft ?? format.money(quote.sell, { bare: true })}
            onFocus={() => setDraft(String(quote.sell.minor / 100))}
            onChange={(e) => setDraft(e.target.value)}
            onBlur={commit}
            onKeyDown={(e) => {
              if (e.key === 'Enter') e.currentTarget.blur();
              if (e.key === 'Escape') {
                setDraft(null);
                e.currentTarget.blur();
              }
            }}
          />
        ) : (
          <MoneyText value={quote.sell} bare />
        )}
        {changeBps !== null && changeBps !== 0 ? <Delta bps={changeBps} /> : null}
      </div>

      <div className="pd-board__margin right" role="cell">
        {belowCost ? (
          <Stamp tone="alert" size="sm" dot title={t('marginWarning')}>
            <IconWarn />
          </Stamp>
        ) : (
          <span className="num">{margin === null ? '—' : format.percentFromBps(margin)}</span>
        )}
      </div>

      <div className="pd-board__supply" role="cell">
        <Stamp
          tone={
            quote.availability === 'none'
              ? 'alert'
              : quote.availability === 'scarce'
                ? 'warn'
                : quote.availability === 'tight'
                  ? 'info'
                  : 'neutral'
          }
          size="sm"
        >
          {t(`availabilityValue.${quote.availability}`)}
        </Stamp>
        {quote.note ? <span className="pd-board__note truncate">{quote.note}</span> : null}
      </div>
    </div>
  );
}
